import ConfigUtils from "./Config";

// Avisos al guardar citas y clases. La API comprueba al guardar y, si el profesional tiene un no disponible, es festivo o
// queda fuera del horario del profesional (409, códigos NO_DISPONIBLE, FESTIVO y FUERA_HORARIO) o la sala está ocupada
// (418), la pantalla pregunta si se guarda igualmente y reenvía sin esa comprobación. El profesional ocupado no se
// pregunta: la API lo rechaza (400) salvo que la clínica permita solapes.

export type TipoConflicto = 'noDisponible' | 'festivo' | 'fueraHorario' | 'sala';
export interface ConflictoAgenda { tipo: TipoConflicto; titulo: string; mensaje: string }

interface CoincidenciaNoDisponible { start: string; end: string; noDisponibleStart: string; noDisponibleEnd: string }
interface CoincidenciaFestivo { start: string; end: string; nombre: string }
interface CoincidenciaFueraHorario { start: string; end: string; tramos: { inicio: string; fin: string }[] }
interface RespuestaConflicto {
    code?: string;
    especialistaId?: number | null;
    total?: number;
    coincidencias?: CoincidenciaNoDisponible[];
    fechas?: string[];
}
interface RespuestaFestivo { total?: number; coincidencias?: CoincidenciaFestivo[] }
interface RespuestaFueraHorario { especialistaId?: number | null; total?: number; coincidencias?: CoincidenciaFueraHorario[] }

// Las horas se muestran como en la agenda: en la hora del navegador.
const hora = (fecha: string) => new Date(fecha).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
const diaCorto = (fecha: string) => new Date(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
const diaYHora = (fecha: string) => `${new Date(fecha).toLocaleDateString('es-ES')} ${hora(fecha)}`;
const mismoDia = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

const MAX_FECHAS = 5;
const listaFechas = (fechas: string[]) => {
    const mostradas = fechas.slice(0, MAX_FECHAS).map(diaCorto).join(', ');
    return fechas.length > MAX_FECHAS ? `${mostradas} y ${fechas.length - MAX_FECHAS} más` : mostradas;
};

const nombreProfesional = (especialistaId?: number | null) => {
    // El titular es el especialista 0: se compara con null, nunca por valor falsy.
    if (especialistaId === undefined || especialistaId === null) return 'El profesional';
    return ConfigUtils.getSanitarios().find((s: { internalID: number }) => s.internalID === especialistaId)?.firstName || 'El profesional';
};

const mensajeNoDisponible = (datos: RespuestaConflicto): string => {
    const coincidencias = datos.coincidencias ?? [];
    const nombre = nombreProfesional(datos.especialistaId);
    if ((datos.total ?? 1) > 1) {
        return `${nombre} no está disponible en ${coincidencias.length} de las fechas: ${listaFechas(coincidencias.map(c => c.start))}. ¿Quieres guardar igualmente?`;
    }
    const c = coincidencias[0];
    if (!c) return `${nombre} no está disponible a esta hora. ¿Quieres guardar igualmente?`;
    const periodo = mismoDia(c.noDisponibleStart, c.noDisponibleEnd)
        ? `a esta hora (${hora(c.noDisponibleStart)}–${hora(c.noDisponibleEnd)})`
        : `del ${diaYHora(c.noDisponibleStart)} al ${diaYHora(c.noDisponibleEnd)}`;
    return `${nombre} no está disponible ${periodo}. ¿Quieres guardar igualmente?`;
};

const diaLargo = (fecha: string) => new Date(fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
const diaSemana = (fecha: string) => new Date(fecha).toLocaleDateString('es-ES', { weekday: 'long' });
const textoTramos = (tramos: { inicio: string; fin: string }[]) =>
    tramos.map(t => `${t.inicio.replace(/^0(\d)/, '$1')}–${t.fin.replace(/^0(\d)/, '$1')}`).join(' · ');

const mensajeFestivo = (datos: RespuestaFestivo): string => {
    const coincidencias = datos.coincidencias ?? [];
    if ((datos.total ?? 1) > 1) {
        return `${coincidencias.length} de las fechas son festivo: ${listaFechas(coincidencias.map(c => c.start))}. ¿Quieres guardar igualmente?`;
    }
    const c = coincidencias[0];
    if (!c) return 'Ese día es festivo. ¿Quieres guardar igualmente?';
    return `El ${diaLargo(c.start)} es festivo (${c.nombre}). ¿Quieres guardar igualmente?`;
};

const mensajeFueraHorario = (datos: RespuestaFueraHorario): string => {
    const coincidencias = datos.coincidencias ?? [];
    const nombre = nombreProfesional(datos.especialistaId);
    if ((datos.total ?? 1) > 1) {
        return `${nombre} no trabaja a esa hora en ${coincidencias.length} de las fechas: ${listaFechas(coincidencias.map(c => c.start))}. ¿Quieres guardar igualmente?`;
    }
    const c = coincidencias[0];
    if (!c) return `${nombre} no trabaja a esta hora. ¿Quieres guardar igualmente?`;
    const horario = c.tramos.length > 0
        ? `su horario del ${diaSemana(c.start)}: ${textoTramos(c.tramos)}`
        : `el ${diaSemana(c.start)} no tiene horario`;
    return `${nombre} no trabaja a esta hora (${horario}). ¿Quieres guardar igualmente?`;
};

const mensajeSala = (datos: RespuestaConflicto): string => {
    const fechas = datos.fechas ?? [];
    if ((datos.total ?? 1) > 1 && fechas.length > 0) {
        return `Esta sala está ocupada por otra actividad en ${fechas.length} de las fechas: ${listaFechas(fechas)}. ¿Está seguro de que desea juntar las actividades en la misma sala?`;
    }
    return 'Esta sala está ocupada por otra actividad ¿Está seguro de que desea juntar las actividades en la misma sala?';
};

// Conflicto que la pantalla tiene que preguntar, o null si el error es otro.
export const leerConflicto = (e: unknown): ConflictoAgenda | null => {
    const respuesta = (e as { response?: { status?: number; data?: RespuestaConflicto } })?.response;
    if (respuesta?.status === 409 && respuesta.data?.code === 'NO_DISPONIBLE') {
        return { tipo: 'noDisponible', titulo: 'PROFESIONAL NO DISPONIBLE', mensaje: mensajeNoDisponible(respuesta.data) };
    }
    if (respuesta?.status === 409 && respuesta.data?.code === 'FESTIVO') {
        return { tipo: 'festivo', titulo: 'DÍA FESTIVO', mensaje: mensajeFestivo(respuesta.data as RespuestaFestivo) };
    }
    if (respuesta?.status === 409 && respuesta.data?.code === 'FUERA_HORARIO') {
        return { tipo: 'fueraHorario', titulo: 'FUERA DE HORARIO', mensaje: mensajeFueraHorario(respuesta.data as RespuestaFueraHorario) };
    }
    if (respuesta?.status === 418) {
        return { tipo: 'sala', titulo: 'SALA OCUPADA', mensaje: mensajeSala(respuesta.data ?? {}) };
    }
    return null;
};

// Lo que el usuario ya ha confirmado en este intento de guardar ("guardar igualmente"): esas comprobaciones no se
// repiten al reenviar. Se reinicia al guardar, al cancelar o con cualquier otro error.
export interface Confirmaciones { sala: boolean; noDisponible: boolean; festivo: boolean; fueraHorario: boolean }
export const sinConfirmaciones = (): Confirmaciones => ({ sala: false, noDisponible: false, festivo: false, fueraHorario: false });

// Lo que se pide comprobar a la API: todo lo que el usuario aún no ha confirmado
export interface Comprobaciones { validateSala: boolean; validateNoDisponible: boolean; validateFestivo: boolean; validateFueraHorario: boolean }
export const comprobaciones = (c: Confirmaciones): Comprobaciones => ({
    validateSala: !c.sala,
    validateNoDisponible: !c.noDisponible,
    validateFestivo: !c.festivo,
    validateFueraHorario: !c.fueraHorario,
});
// Sin ningún aviso (al guardar los datos clínicos de una cita, sin cambiar su hora)
export const SIN_COMPROBAR: Comprobaciones = { validateSala: false, validateNoDisponible: false, validateFestivo: false, validateFueraHorario: false };
